import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Card,
    CardContent,
    Box,
    Divider,
    LinearProgress,
    TextField
} from '@material-ui/core';
import { Cancel, NavigateBefore, NavigateNext, Send } from '@material-ui/icons';
import AudioModal from '../AudioModal';
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    modal: {
        backgroundColor: theme.palette.background.paper,
    },
    dialogPaper: {
        borderRadius: 12,
        overflow: 'hidden',
    },
    card: {
        borderRadius: 12,
        boxShadow: 'none',
        backgroundColor: 'transparent',
    },
    mediaContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        backgroundColor: theme.palette.grey[100],
        position: 'relative',
        overflow: 'hidden',
    },
    mediaContent: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
    },
    fileName: {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.background.paper,
        textAlign: 'center',
        fontWeight: 500,
    },
    captionInput: {
        padding: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: 20,
            backgroundColor: theme.palette.background.paper,
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1.5, 2),
        },
    },
    actions: {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    navigationButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    disabledButton: {
        color: theme.palette.action.disabled,
    },
    activeButton: {
        color: theme.palette.primary.main,
    },
    paginationText: {
        margin: theme.spacing(0, 1),
        color: theme.palette.text.secondary,
    },
}));

const MessageUploadMedias = ({ isOpen, files, onClose, onSend, onCancelSelection, uploadProgress, onCancelUpload }) => {
    const classes = useStyles();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [captions, setCaptions] = useState(files.map(() => ''));

    const isUploading = uploadProgress !== null && uploadProgress !== undefined;

    // Cria object URLs uma unica vez por arquivo e revoga no unmount
    // para nao vazar memoria nem recarregar a midia a cada render.
    const fileUrls = useMemo(
        () => files.map(file => URL.createObjectURL(file)),
        [files]
    );

    useEffect(() => {
        return () => {
            fileUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [fileUrls]);

    const handleClose = () => {
        // Nao fecha o modal durante upload para evitar perder o controle de cancelamento
        if (isUploading) return;
        onClose();
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < files.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleCaptionChange = (e) => {
        const value = e.target.value;
        setCaptions((prevCaptions) => {
            const updatedCaptions = [...prevCaptions];
            updatedCaptions[currentIndex] = value;
            return updatedCaptions;
        });
    };

    const handleSend = () => {
        const selectedMedias = files.map((file, index) => ({
            file,
            caption: captions[index],
        }));
        onSend(selectedMedias);
        handleClose();
    };

    const renderFileContent = () => {
        const currentFile = files[currentIndex];
        const currentUrl = fileUrls[currentIndex];
        if (!currentFile) return null;

        if (currentFile.type.startsWith('image')) {
            return (
                <>
                    <Box className={classes.mediaContainer}>
                        <img
                            alt={`Imagem ${currentIndex + 1}`}
                            src={currentUrl}
                            className={classes.mediaContent}
                        />
                    </Box>
                    <Typography variant="subtitle1" className={classes.fileName}>
                        {currentFile.name}
                    </Typography>
                </>
            );
        }
        if (currentFile.type.startsWith('video')) {
            return (
                <>
                    <Box className={classes.mediaContainer}>
                        <video
                            src={currentUrl}
                            controls
                            preload="metadata"
                            volume={localStorage.getItem("volume")}
                            className={classes.mediaContent}
                        />
                    </Box>
                    <Typography variant="subtitle1" className={classes.fileName}>
                        {currentFile.name}
                    </Typography>
                </>
            );
        }
        if (currentFile.type.startsWith('audio')) {
            return (
                <>
                    <Box className={classes.mediaContainer}>
                        <AudioModal url={currentUrl} />
                    </Box>
                    <Typography variant="subtitle1" className={classes.fileName}>
                        {currentFile.name}
                    </Typography>
                </>
            );
        }
        // PDF e outros tipos sem visualizacao especifica
        return (
            <>
                <Box className={classes.mediaContainer}>
                    <CardContent>
                        <Typography variant="h6" color="textSecondary" align="center">
                            Visualização não disponível
                        </Typography>
                        <Typography variant="body2" color="textSecondary" align="center">
                            {currentFile.type}
                        </Typography>
                    </CardContent>
                </Box>
                <Typography variant="subtitle1" className={classes.fileName}>
                    {currentFile.name}
                </Typography>
            </>
        );
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && event.shiftKey) {
            return;
        }
        if (isUploading) {
            // Durante upload somente Escape cancela o envio
            if (event.key === 'Escape' && onCancelUpload) {
                onCancelUpload();
            }
            return;
        }
        switch (event.key) {
            case 'Escape':
                onCancelSelection();
                break;
            case 'Enter':
                handleSend();
                break;
            case 'ArrowRight':
                handleNext();
                break;
            case 'ArrowLeft':
                handlePrev();
                break;
            default:
                break;
        }
    };

    return (
        <Dialog
            open={isOpen}
            fullWidth
            maxWidth="md"
            onClose={handleClose}
            classes={{ paper: classes.dialogPaper }}
        >
            <Card className={classes.card}>
                {renderFileContent()}

                <Divider />

                <Box className={classes.captionInput}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Adicione uma legenda (opcional)"
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={captions[currentIndex]}
                        onChange={handleCaptionChange}
                        autoFocus
                        onKeyDown={handleKeyDown}
                        disabled={isUploading}
                    />
                </Box>

                {isUploading && (
                    <Box px={2} pb={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" color="textSecondary">
                                Enviando arquivo...
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {uploadProgress}%
                            </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                    </Box>
                )}

                <Box className={classes.actions}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center">
                            <IconButton
                                onClick={handlePrev}
                                disabled={isUploading || currentIndex === 0}
                                className={currentIndex === 0 ? classes.disabledButton : classes.navigationButton}
                            >
                                <NavigateBefore />
                            </IconButton>

                            <Typography variant="body2" className={classes.paginationText}>
                                {currentIndex + 1} / {files.length}
                            </Typography>

                            <IconButton
                                onClick={handleNext}
                                disabled={isUploading || currentIndex === files.length - 1}
                                className={currentIndex === files.length - 1 ? classes.disabledButton : classes.navigationButton}
                            >
                                <NavigateNext />
                            </IconButton>
                        </Box>

                        <Box display="flex">
                            {isUploading ? (
                                <Button
                                    onClick={onCancelUpload}
                                    startIcon={<Cancel />}
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                >
                                    Cancelar envio
                                </Button>
                            ) : (
                                <>
                                    <IconButton onClick={onCancelSelection} className={classes.navigationButton}>
                                        <Cancel />
                                    </IconButton>
                                    <IconButton onClick={handleSend} className={classes.activeButton}>
                                        <Send />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Card>
        </Dialog>
    );
};

export default MessageUploadMedias;